import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Exchange,
  ExtendedExchange,
  KycResponse,
  KycStatus,
  LoginPayload,
  LoginResponse,
  SessionsTypes,
  SignupPayload,
  UserAccessLevel,
  UserData,
} from './types';
import {
  useActivateExchangeMutation,
  useGoogleOauthMutation,
  useKycStatusesQuery,
  useLazySessionQuery,
  useLazyUserQuery,
  useLoginMutation,
  useLogoutMutation,
  useResendCodeEnableUserMutation,
  useResendCodeMutation,
  useResendCodeNewUserMutation,
  useResendKnownDeviceCodeMutation,
  useSendCodeWithdrawMutation,
  useSessionQuery,
  useSetLocaleMutation,
  useSignupMutation,
  useVerify2FAMutation,
  useVerifyCodeMutation,
  useVerifyCodeNewUserMutation,
  useVerifyKnownDeviceMutation,
} from './api';
import {
  activateExchange,
  setAccessToken,
  setActiveExchangeInternalId,
  setLocale,
  setSessionExpiryDate,
  setUser,
} from './store';
import { useDispatch, useSelector } from 'react-redux';
import { gtagEvent, trackNewSession, tzTrack } from '../../util/analytics';
import { startLiveSession } from '../../util/liveSession';
import { RootState } from '../store';
import { useTranslation } from 'react-i18next';
import { ShowFnOutput } from 'mui-modal-provider';
import AuthVerifyModal from '../../views/Auth/components/AuthVerifyModal';
import { getImageOfAccount } from '../../util/images';
import { useLazyTraderServicesQuery } from '../service/api';
import { QueryReturnTypeBasic } from 'util/queryReturnType';
import { useZAlert, useZModal } from 'components/ZModal/use';
import { useNavigate } from 'react-router-dom';
import { clearUserSession, extractStatus } from './util';
import { junkyard } from '../../util/junkyard';
import AuthorizationModal from 'views/Auth/components/AuthorizationModal';
import { isFeatureOn, whitelabel } from '../../whitelabel';
import { Features, Mandatory2FA } from '../../whitelabel/type';
import { ROUTE_2FA } from 'routes';
import { useGoogleLogin } from '@react-oauth/google';
import { useToast } from '@ui/hooks/useToast';
import useTrackEvent from '../../components/Navigation/Tracker/use';
import Cookies from 'js-cookie';
import baseApiPs2 from '../baseApiPs2';
import TosVerifyModal from '../../views/Auth/components/TosVerifyModal';
import { RtkQueryLike } from '@ui/utils/rtk';

const useStartSession = () => {
  const { showModal } = useZModal();
  const dispatch = useDispatch();
  const [loadSession] = useLazySessionQuery();
  const [loadTraderServices] = useLazyTraderServicesQuery();
  const [loadUser] = useLazyUserQuery();
  const toast = useToast();
  const { t } = useTranslation('auth');
  const updateCurrentLocale = useChangeLocale(true);
  const cleanUpSessionAfterFailedAttempt = () =>
    dispatch(setAccessToken(undefined));

  return async (
    user: { token: string; userId?: string } & Partial<LoginResponse>,
    eventType: SessionsTypes,
  ) => {
    dispatch(setAccessToken(user.token));
    user.userId && tzTrack({ userId: user.userId });
    const emailUnconfirmed =
      user.emailUnconfirmed && isFeatureOn(Features.EmailVerification);
    const needsAuthModal =
      user.ask2FA || user.isUnknownDevice || user.disabled || emailUnconfirmed;

    const needsTosModal = user.areTOSAccepted === false;

    if (needsTosModal) {
      let modal: ShowFnOutput<void>;
      try {
        await new Promise<void>((onSuccess, onFailure) => {
          modal = showModal(TosVerifyModal, {
            onSuccess,
            onFailure,
            close: () => {
              modal.destroy();
            },
          });
        });
      } catch (e) {
        toast.error(t('auth:tos-not-accepted'));
        cleanUpSessionAfterFailedAttempt();
        return;
      }
      gtagEvent('tos_approved');
    }

    if (needsAuthModal) {
      let modal: ShowFnOutput<void>;
      try {
        await new Promise<void>((resolve, reject) => {
          modal = showModal(AuthVerifyModal, {
            user,
            onSuccess: resolve,
            onFailure: reject,
            close: () => {
              modal.destroy();
            },
          });
        });
      } catch (e) {
        cleanUpSessionAfterFailedAttempt();
        return;
      }
      eventType === 'signup' && gtagEvent('otp_verified');
    }

    const [, userData] = await Promise.all([
      loadSession()
        .unwrap()
        .then(({ validUntil }) => dispatch(setSessionExpiryDate(validUntil))),
      loadUser().unwrap(),
      loadTraderServices().unwrap(),
    ]);

    dispatch(setUser(userData));
    startLiveSession(userData);
    trackNewSession(
      userData,
      emailUnconfirmed ? SessionsTypes.Signup : eventType,
    );
    junkyard.set('hasLoggedIn', 'true');
    updateCurrentLocale(userData.locale);
  };
};

export const useSignup = (): [
  { loading: boolean },
  (payload: SignupPayload) => Promise<void>,
] => {
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();
  const [signup] = useSignupMutation();
  const startSession = useStartSession();

  return [
    { loading },
    async (payload: SignupPayload) => {
      setLoading(true);
      try {
        const user = await signup({
          ...payload,
          // this should be redundant because we only have 2-letter locales
          locale: i18n.language?.slice(0, 2),
        }).unwrap();
        await startSession(
          {
            ...user,
            emailUnconfirmed: isFeatureOn(Features.EmailVerification),
          },
          SessionsTypes.Signup,
        );
      } finally {
        setLoading(false);
      }
    },
  ];
};

export const useAuthenticate = (): [
  { loading: boolean },
  (payload: LoginPayload) => Promise<void>,
] => {
  const [login] = useLoginMutation();
  const startSession = useStartSession();

  const [loading, setLoading] = useState(false);

  // can't use useAsyncFn because https://github.com/streamich/react-use/issues/1768
  return [
    { loading },
    async (payload: LoginPayload) => {
      setLoading(true);

      try {
        const user = await login({
          ...payload,
        }).unwrap();
        await startSession(user, SessionsTypes.Login);
        junkyard.set('lastUsedLoginMethod', 'email');
        setLoading(false);
      } catch (e) {
        setLoading(false);
        throw e;
      }
    },
  ];
};

export function useLogout(performRequest = true): () => void {
  const dispatch = useDispatch();
  const [logoutRequest] = useLogoutMutation();
  const navigate = useNavigate();

  return () => {
    if (performRequest) {
      logoutRequest();
    }
    dispatch(baseApiPs2.util.resetApiState());
    clearUserSession(dispatch, true);
    navigate('/login');
  };
}

export function useIsAuthenticated(): boolean {
  const user = useSelector((state: RootState) => state.user)?.user;
  return !!user;
}

export function useUserAccessLevel(): UserAccessLevel {
  return useSelector((state: RootState) => state.user)?.user?.accessLevel;
}

export function useCurrentUser(): UserData | Partial<UserData> {
  return (
    useSelector((state: RootState) => state.user)?.user || ({} as UserData)
  );
}

export function useUserSuccessFeeDiscount() {
  const successFeeDiscount =
    useSelector((state: RootState) => state.user)?.user?.subscription
      ?.successFeeDiscountPct || 0;

  if (!isFeatureOn(Features.Subscriptions)) {
    return 0;
  }
  return successFeeDiscount;
}

export const useVerify2FA: typeof useVerify2FAMutation = useVerify2FAMutation;
export const useVerifyEmailNewUser: typeof useVerifyCodeNewUserMutation =
  useVerifyCodeNewUserMutation;
export const useVerifyEmail: typeof useVerifyCodeMutation =
  useVerifyCodeMutation;
export const useSendCodeWithdraw: typeof useSendCodeWithdrawMutation =
  useSendCodeWithdrawMutation;
export const useVerifyEmailKnownDevice: typeof useVerifyKnownDeviceMutation =
  useVerifyKnownDeviceMutation;
export const useResendCodeEnableUser: typeof useResendCodeEnableUserMutation =
  useResendCodeEnableUserMutation;
export const useResendCode: typeof useResendCodeMutation =
  useResendCodeMutation;
export const useResendCodeNewUser: typeof useResendCodeNewUserMutation =
  useResendCodeNewUserMutation;
export const useResendKnownDeviceCode: typeof useResendKnownDeviceCodeMutation =
  useResendKnownDeviceCodeMutation;

/*
 * We have two scenarios here:
 * - the user manually changes the language
 * - we get the locale value from the backend and uodate it
 */
export function useChangeLocale(
  isSavedLocale?: boolean,
): (locale: string) => void {
  const [save] = useSetLocaleMutation();
  const { i18n } = useTranslation();
  const selectedLocale = useSelector((state: RootState) => state.user)?.locale;
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();

  return (locale: string) => {
    // Suppose a user is logged out, then they change their locale and then log in
    // We should not change their locale to what they had on the backend
    if (!isSavedLocale || !selectedLocale) {
      dispatch(setLocale(locale));
      i18n.changeLanguage(locale);
    }
    if (isAuthenticated && !isSavedLocale) {
      save({ locale });
    } else if (isSavedLocale && locale !== selectedLocale && selectedLocale) {
      // we do not check for isAuthenticated here because we know the user is authenticated because
      // we come here with the saved locale.
      // the isAuthenticated flag will be false though because we're ina  process of getting authenticated
      save({ locale: selectedLocale });
    }
  };
}

export const useGetExchangeByInternalId = (): ((
  internalId?: string,
) => ExtendedExchange | undefined) => {
  const { user, activeExchangeInternalId } = useSelector(
    (state: RootState) => state.user,
  );
  return (internalId) => {
    if (!user?.exchanges) return undefined;
    const id = internalId || activeExchangeInternalId;
    const exchange =
      id && user.exchanges?.find((x: Exchange) => x.internalId === id);
    if (!exchange) return undefined;

    return {
      ...exchange,
      image: getImageOfAccount(user.exchanges.indexOf(exchange)),
    };
  };
};

export function useActiveExchange(): ExtendedExchange | undefined {
  const { user, activeExchangeInternalId: internalId } = useSelector(
    (state: RootState) => state.user,
  );
  const dispatch = useDispatch();

  const defaultExchange = user?.exchanges?.[0];
  const exchange = user?.exchanges?.find((x) => x.internalId === internalId);

  useEffect(() => {
    if (!exchange && defaultExchange) {
      dispatch(setActiveExchangeInternalId(defaultExchange.internalId));
    }
  }, [exchange]);

  const result = exchange || defaultExchange || undefined;
  return useMemo(
    () =>
      result &&
      user?.exchanges && {
        ...result,
        image: getImageOfAccount(user.exchanges.indexOf(result)),
      },
    [result, user?.exchanges],
  );
}

export function useSelectExchange(): (exchangeInternalId: string) => void {
  const dispatch = useDispatch();
  return (exchangeInternalId) =>
    dispatch(setActiveExchangeInternalId(exchangeInternalId));
}

export function useActivateExchange(
  exchangeInternalId?: string,
): QueryReturnTypeBasic<void> {
  const exchange = useActiveExchange();
  const [activate, result] = useActivateExchangeMutation();
  const dispatch = useDispatch();

  const internalId = exchangeInternalId || exchange?.internalId;

  useEffect(() => {
    if (exchangeInternalId || (exchange && !exchange.activated)) {
      activate({
        exchangeInternalId: internalId,
      }).then(() => {
        dispatch(activateExchange(internalId));
      });
    }
  }, [internalId]);

  return result;
}

export function useMaybeMakeSureSessionIsAlive(makeSure: boolean): void {
  const isAuthenticated = useIsAuthenticated();
  useSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    // we do not want to check that is the app is already aware that the user has been logged out
    skip: !makeSure || !isAuthenticated,
  });
}

export function useCheckAuthorization({
  status,
  sendCode,
  codeReason,
  title,
  description,
}: {
  status: QueryReturnTypeBasic<unknown>;
  sendCode?: () => void;
  codeReason?: string;
  title?: string;
  description?: string;
}): (action: (code?: string, code2FA?: string) => void) => () => void {
  const { showModal, updateModal, destroyModal } = useZModal();
  const modalId = useRef<null | string>(null);

  // Update prop: https://github.com/Quernest/mui-modal-provider/issues/2
  useEffect(() => {
    if (modalId.current) {
      updateModal(modalId.current, {
        status,
      });
    }
  }, [status]);

  return (action) => {
    const modal = showModal(AuthorizationModal, {
      status,
      action,
      sendCode,
      codeReason,
      title,
      description,
      TransitionProps: {
        onClose: () => {
          modalId.current = null;
        },
      },
    });

    modalId.current = modal.id;
    return destroyModal.bind(null, modal.id);
  };
}

export function useMandatory2FA(mandatory2FA: Mandatory2FA) {
  const { t } = useTranslation('auth');
  const user = useCurrentUser();
  const showAlert = useZAlert();
  const navigate = useNavigate();

  return (checkOnly?: boolean, closeCallback?: () => void) => {
    const ok =
      !whitelabel.mandatory2FA?.includes(mandatory2FA) || user['2FAEnable'];
    if (!ok && !checkOnly) {
      showAlert({
        title: t('need-2fa.title'),
        okLabel: t('need-2fa.enable'),
        description: t('need-2fa.description'),
        okAction: () => {
          closeCallback?.();
          navigate(ROUTE_2FA);
        },
      });
    }
    return ok;
  };
}

export function useGoogleAuth(): {
  isLoading: boolean;
  login: () => void;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [googleAuth] = useGoogleOauthMutation();
  const toast = useToast();
  const { t } = useTranslation('auth');
  const startSession = useStartSession();
  const [loadUser] = useLazyUserQuery();
  const trackEvent = useTrackEvent();
  const isAuthenticated = useIsAuthenticated();
  const performLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    redirect_uri: `${location.protocol}:/${location.host}`,
    onSuccess: async (data) => {
      try {
        const { justLinked, ...result } = await googleAuth({
          ...data,
          ref: Cookies.get('ref'),
          subtrack: Cookies.get('subtrack'),
        }).unwrap();
        if (isAuthenticated) {
          await loadUser().unwrap();
        } else {
          await startSession(result, result.action);
          junkyard.set('lastUsedLoginMethod', 'google');
        }
        if (justLinked === true) {
          toast.success(t('google-auth.linked-successfully'));
        }
      } catch (e) {
        // Do nothing
      } finally {
        setIsLoading(false);
      }
    },
    onError: (data) => {
      switch (data?.type) {
        case 'access_denied':
          trackEvent('oauth_permission_denied');
          toast.error(t('google-auth.access-denied'));
          break;
        case 'popup_closed':
          trackEvent('oauth_popup_closed');
          // Show no error
          break;
        default:
          toast.error(t('google-auth.generic-error'));
      }
      setIsLoading(false);
      console.error(data);
    },
    onNonOAuthError: (data) => {
      toast.error(t('google-auth.generic-error'));
      setIsLoading(false);
      console.error(data);
    },
  });

  return {
    login: () => {
      setIsLoading(true);
      performLogin();
    },
    isLoading,
  };
}

// a solution I find elegant to avoid making any changes in the code
// related to KYC notifications, KYC status updates, etc
export const useUserKycStatusQuery: RtkQueryLike<KycResponse, {}> = (options?: {
  skip?: boolean;
  pollingInterval?: number;
}) => {
  const [skipShufti, setSkipShufti] = useState(false);
  const [skipSumsub, setSkipSumsub] = useState(false);

  const shufti = useKycStatusesQuery('shufti', {
    ...(options || {}),
    skip: options?.skip || skipShufti,
  });
  const sumsub = useKycStatusesQuery('sumsub', {
    ...(options || {}),
    skip: options?.skip || skipSumsub,
  });

  const sumsubStatus = extractStatus(sumsub?.data);
  const shuftiStatus = extractStatus(shufti?.data);

  useEffect(() => {
    if (sumsubStatus && shuftiStatus) {
      const shouldUseSumsub =
        sumsubStatus !== KycStatus.NOT_STARTED ||
        shuftiStatus === KycStatus.NOT_STARTED;
      setSkipShufti(shouldUseSumsub);
      setSkipSumsub(!shouldUseSumsub);
    }
  }, [sumsubStatus]);

  const resultingKyc =
    sumsubStatus === KycStatus.NOT_STARTED ? shufti.data : sumsub.data;

  return {
    isFetching: shufti.isFetching || sumsub.isFetching,
    isLoading: shufti.isLoading || sumsub.isLoading,
    isSuccess: shufti.isSuccess || sumsub.isSuccess,
    isError: shufti.isError || sumsub.isError,
    error: shufti.error || sumsub.error,
    refetch: () => {
      shufti.refetch();
      sumsub.refetch();
    },
    data: resultingKyc,
  };
};

export const useTraderKycStatusQuery = (options?: {
  skip?: boolean;
  pollingInterval?: number;
}): ReturnType<typeof useKycStatusesQuery> => {
  return useKycStatusesQuery('binance', options);
};

// TODO: useLazyKycLinkQuery
